import ConditionalWrapper from "@splitfire-agency/raiden-library/dist/components/ConditionalWrapper";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import useRequest from "../../../hooks/useRequest";
import { useApiError, withApiError } from "../ApiError";

/**
 * @template {import("react-hook-form").FieldValues} [TFieldValues=any]
 * @typedef {object} Props
 * @property {boolean} [withForm]
 * @property {string} [formId]
 * @property {TFieldValues} defaultValues
 * @property {(params: {values: TFieldValues}) => { url: string, init: (RequestInit | { body: object }), options?: import("@raiden/library/hooks/useApiFetcher").FetchOptions }} getRequestParams
 * @property {(params: { response: import("../../../types/Api/ApiResponse").ApiResponse<any>, form: import("react-hook-form").UseFormReturn<TFieldValues>}) => void} [onSuccess]
 * @property {(params: { response: import("../../../types/Api/ApiResponse").ApiResponse<any>, form: import("react-hook-form").UseFormReturn<TFieldValues>}) => void} [onError]
 * @property {boolean} [showSuccessToast]
 * @property {string} [successMessage]
 * @property {(params: {
 * form: import("react-hook-form").UseFormReturn<TFieldValues>,
 * submit: () => void,
 * isLoading: boolean,
 * }) => import("react").ReactElement} children
 */
/** @typedef {import("react").FunctionComponent<Props<String>>} Test */
/**
 * @template {import("react-hook-form").FieldValues} [TFieldValues=any]
 * @param {Props<TFieldValues>} props
 */
function _FormHandler({
  withForm = true,
  formId,
  defaultValues,
  getRequestParams,
  onSuccess,
  onError,
  showSuccessToast = true,
  successMessage,
  children,
}) {
  const intl = useIntl();

  const { resolveApiError } = useApiError();

  const form = useForm({
    // @ts-ignore
    defaultValues,
  });
  const { handleSubmit } = form;

  const { request, isLoading, toastSuccess } = useRequest();

  const { resetApiErrors } = useApiError();

  const submit = useMemo(() => {
    return handleSubmit((values) => {
      const params = getRequestParams({ values });
      request(params.url, params.init, params.options)
        .then((response) => {
          showSuccessToast &&
            toastSuccess({
              title:
                successMessage ??
                intl.formatMessage({
                  defaultMessage:
                    "Les modifications ont bien été prises en compte.",
                }),
            });
          resetApiErrors();
          onSuccess?.({ form, response });
        })
        .catch((response) => {
          resolveApiError({ form, response });
          onError?.({ form, response });
        });
    });
  }, [
    form,
    getRequestParams,
    handleSubmit,
    intl,
    onError,
    onSuccess,
    request,
    resetApiErrors,
    resolveApiError,
    showSuccessToast,
    successMessage,
    toastSuccess,
  ]);

  return (
    <FormProvider {...form}>
      <ConditionalWrapper
        hasWrapper={withForm === true}
        wrapper={(children) => (
          <form id={formId} onSubmit={submit}>
            {children}
          </form>
        )}>
        {children({
          form,
          submit,
          isLoading,
        })}
      </ConditionalWrapper>
    </FormProvider>
  );
}

/**
 * @type {<TFieldValues extends import("react-hook-form").FieldValues>(props: Props<TFieldValues>) => React.ReactElement}
 */
// @ts-ignore
export const FormHandler = withApiError(_FormHandler);
